/**
 * Keyboard Configuration
 * Provides configurable keyboard shortcuts with persistence
 * @license GPL-3.0-or-later
 */

import { escapeHtml } from './html-utils.js';

/**
 * Storage key for keyboard shortcuts
 */
const STORAGE_KEY = 'openscad-forge-keyboard-shortcuts';

/**
 * Default keyboard shortcuts aligned to OpenSCAD desktop (2026.02.19).
 * Format: { action: { key, ctrl?, shift?, alt?, meta?, description } }
 */
export const DEFAULT_SHORTCUTS = {
  // Rendering (F-keys match OpenSCAD desktop)
  render: {
    key: 'F6',
    description: 'Render model (full quality)',
  },
  preview: {
    key: 'F5',
    description: 'Preview model (fast)',
  },
  reloadAndPreview: {
    key: 'F4',
    description: 'Reload file and preview',
  },
  cancelRender: {
    key: 'Escape',
    description: 'Cancel current render',
  },

  renderAlt: {
    key: 'Enter',
    ctrl: true,
    description: 'Render or download (alternative key)',
  },
  generateShortcut: {
    key: 'g',
    description: 'Generate the model',
  },

  // Downloads
  download: {
    key: 'F7',
    description: 'Export STL / download rendered model',
  },
  downloadShortcut: {
    key: 'd',
    description: 'Download the rendered model',
  },
  // Editor
  toggleExpertMode: {
    key: 'e',
    ctrl: true,
    description: 'Toggle Editor panel',
  },

  // View controls
  focusMode: {
    key: 'f',
    description: 'Toggle focus mode (maximize preview)',
  },
  // The Alt View's character size: the bare minus and equals keys, the
  // same pair as the stepper's buttons, free of Chrome's own shortcuts.
  altViewSmaller: {
    key: '-',
    description: 'Alt View: smaller or larger characters',
  },
  altViewLarger: {
    key: '=',
    description: 'Alt View: smaller or larger characters',
  },
  toggleParameters: {
    key: 'b',
    ctrl: true,
    description: 'Toggle Customizer panel',
  },
  resetView: {
    key: 'r',
    description: 'Reset camera view',
  },

  // Camera view presets (matches OpenSCAD desktop Ctrl+numpad layout)
  viewTop: {
    key: '4',
    ctrl: true,
    description: 'Top view',
  },
  viewBottom: {
    key: '5',
    ctrl: true,
    description: 'Bottom view',
  },
  viewFront: {
    key: '8',
    ctrl: true,
    description: 'Front view',
  },
  viewBack: {
    key: '9',
    ctrl: true,
    description: 'Back view',
  },
  viewLeft: {
    key: '6',
    ctrl: true,
    description: 'Left view',
  },
  viewRight: {
    key: '7',
    ctrl: true,
    description: 'Right view',
  },
  viewDiagonal: {
    key: '0',
    ctrl: true,
    description: 'Diagonal isometric view',
  },
  viewCenter: {
    key: '0',
    ctrl: true,
    shift: true,
    description: 'Center / reset all view',
  },
  toggleProjection: {
    key: 'p',
    description: 'Toggle perspective/orthographic projection',
  },

  // Parameter controls
  //
  // Undo/redo and the three below live in this registry, not in a separate
  // keydown listener, so they show in the shortcuts modal, can be rebound,
  // and Ctrl+Z does not fire the parameter undo while the user is typing in
  // the code editor.
  undo: {
    key: 'z',
    ctrl: true,
    description: 'Undo the last parameter change',
  },
  redo: {
    key: 'z',
    ctrl: true,
    shift: true,
    description: 'Redo the last parameter change',
  },
  redoAlt: {
    key: 'y',
    ctrl: true,
    description: 'Redo the last parameter change (alternative key)',
  },
  resetAllParams: {
    key: 'r',
    ctrl: true,
    shift: true,
    description: 'Reset all parameters to defaults',
  },
  searchParams: {
    key: 'f',
    ctrl: true,
    shift: true,
    description: 'Focus parameter search',
  },

  // Theme (shifted to avoid OpenSCAD conflicts)
  toggleTheme: {
    key: 't',
    ctrl: true,
    shift: true,
    description: 'Toggle light/dark theme',
  },
  toggleHighContrast: {
    key: 'h',
    ctrl: true,
    shift: true,
    description: 'Toggle high contrast mode',
  },

  // Help
  showShortcutsModal: {
    key: 'k',
    ctrl: true,
    shift: true,
    description: 'Open keyboard shortcuts settings',
  },

  // Saved Projects
  focusSavedProjects: {
    key: 'p',
    ctrl: true,
    shift: true,
    description: 'Focus saved projects list',
  },

  // File actions
  newFile: {
    key: 'n',
    ctrl: true,
    description: 'New file',
  },
  saveFile: {
    key: 's',
    ctrl: true,
    description: 'Save file',
  },
  saveFileAs: {
    key: 's',
    ctrl: true,
    shift: true,
    description: 'Save file as',
  },
  reloadFile: {
    key: 'r',
    ctrl: true,
    description: 'Reload file',
  },
  exportImage: {
    key: 'e',
    ctrl: true,
    shift: true,
    description: 'Export preview as image',
  },

  // Edit actions
  copyViewportImage: {
    key: 'c',
    ctrl: true,
    shift: true,
    description: 'Copy viewport image to clipboard',
  },
  jumpNextError: {
    key: 'e',
    ctrl: true,
    alt: true,
    description: 'Jump to next error',
  },
  jumpPrevError: {
    key: 'e',
    ctrl: true,
    alt: true,
    shift: true,
    description: 'Jump to previous error',
  },
  increaseFontSize: {
    key: '=',
    ctrl: true,
    description: 'Increase editor font size',
    editorOnly: true,
  },
  decreaseFontSize: {
    key: '-',
    ctrl: true,
    description: 'Decrease editor font size',
    editorOnly: true,
  },
  find: {
    key: 'f',
    ctrl: true,
    description: 'Find in editor',
  },
  findNext: {
    key: 'g',
    ctrl: true,
    description: 'Find next match',
  },
  findPrevious: {
    key: 'g',
    ctrl: true,
    shift: true,
    description: 'Find previous match',
  },
  findReplace: {
    key: 'f',
    ctrl: true,
    alt: true,
    description: 'Find and replace in editor',
  },

  // Design tools
  flushCaches: {
    key: 'F9',
    ctrl: true,
    description: 'Flush caches (files, libraries, geometry)',
  },
  showAST: {
    key: 'F10',
    description: 'Display parsed parameter AST',
  },
  checkValidity: {
    key: 'F11',
    description: 'Check geometry validity',
  },

  // Display / View
  viewAll: {
    key: 'v',
    ctrl: true,
    shift: true,
    description: 'Fit model in view',
  },
  toggleEdges: {
    key: '1',
    ctrl: true,
    description: 'Toggle edges overlay',
  },
  toggleAxes: {
    key: '2',
    ctrl: true,
    description: 'Toggle axes display',
  },
  toggleCrosshairs: {
    key: '3',
    ctrl: true,
    description: 'Toggle crosshairs overlay',
  },
  zoomIn: {
    key: ']',
    ctrl: true,
    description: 'Zoom in',
  },
  zoomOut: {
    key: '[',
    ctrl: true,
    description: 'Zoom out',
  },
  // Panel controls
  toggleConsole: {
    key: '1',
    ctrl: true,
    alt: true,
    description: 'Toggle Console panel',
  },
  toggleErrorLog: {
    key: '2',
    ctrl: true,
    alt: true,
    description: 'Toggle Error Log panel',
  },
  toggleCodeEditor: {
    key: '3',
    ctrl: true,
    alt: true,
    description: 'Toggle Editor panel',
  },
  toggleCustomizer: {
    key: '4',
    ctrl: true,
    alt: true,
    description: 'Toggle Customizer panel',
  },
  jumpToPanel: {
    key: 'j',
    ctrl: true,
    description: 'Jump to a panel',
  },
  nextPanel: {
    key: ']',
    ctrl: true,
    alt: true,
    description: 'Focus next panel',
  },
  prevPanel: {
    key: '[',
    ctrl: true,
    alt: true,
    description: 'Focus previous panel',
  },
};

/**
 * Legacy Forge shortcut preset (pre-2026 layout, for existing users who prefer the old keys).
 * Stored as overrides — only the keys that differ from DEFAULT_SHORTCUTS are listed.
 */
export const LEGACY_FORGE_SHORTCUTS = {
  render: { key: 'Enter', ctrl: true },
  preview: { key: 'p', ctrl: true },
  reloadAndPreview: { key: 'F4' },
  download: { key: 'd', ctrl: true },
  viewTop: { key: '5', ctrl: true },
  viewBottom: { key: '0', ctrl: true },
  viewBack: { key: '2', ctrl: true },
  viewLeft: { key: '4', ctrl: true },
  viewRight: { key: '6', ctrl: true },
  viewDiagonal: { key: '1', ctrl: true },
  searchParams: { key: 'f', ctrl: true },
  toggleTheme: { key: 't', ctrl: true },
  toggleHighContrast: { key: 'h', ctrl: true },
  showShortcutsModal: { key: 'k', ctrl: true },
  findReplace: { key: 'h', ctrl: true },
  flushCaches: { key: 'F9' },
  toggleAxes: { key: 'a', ctrl: true, alt: true },
  toggleEdges: { key: 'g', ctrl: true, alt: true },
  nextPanel: { key: ']', ctrl: true },
  prevPanel: { key: '[', ctrl: true },
  // This preset already spends Ctrl+] / Ctrl+[ on panel navigation, so zoom
  // takes the chords the panels vacate. Without this the two pairs would
  // collide and _handleKeydown, which stops at its first match, would leave
  // Next/Previous Panel silently dead for anyone on the legacy preset.
  zoomIn: { key: ']', ctrl: true, alt: true },
  zoomOut: { key: '[', ctrl: true, alt: true },
};

/**
 * Shortcut category definitions for UI organization
 */
export const SHORTCUT_CATEGORIES = {
  rendering: {
    label: 'Rendering',
    actions: [
      'render',
      'renderAlt',
      'generateShortcut',
      'preview',
      'reloadAndPreview',
      'cancelRender',
    ],
  },
  downloads: {
    label: 'Downloads & Export',
    actions: ['download', 'downloadShortcut'],
  },
  view: {
    label: 'View Controls',
    actions: ['focusMode', 'resetView'],
  },
  altView: {
    label: 'Alt View',
    actions: [['altViewSmaller', 'altViewLarger']],
  },
  cameraViews: {
    label: 'Camera Views',
    actions: [
      'viewTop',
      'viewBottom',
      'viewFront',
      'viewBack',
      'viewLeft',
      'viewRight',
      'viewDiagonal',
      'viewCenter',
      'toggleProjection',
    ],
  },
  parameters: {
    label: 'Customizer',
    actions: ['undo', 'redo', 'redoAlt', 'resetAllParams', 'searchParams'],
  },
  theme: {
    label: 'Theme',
    actions: ['toggleTheme', 'toggleHighContrast'],
  },
  help: {
    label: 'Help',
    actions: ['showShortcutsModal'],
  },
  file: {
    label: 'File',
    actions: ['newFile', 'saveFile', 'saveFileAs', 'reloadFile', 'exportImage'],
  },
  editing: {
    label: 'Editing',
    actions: [
      'copyViewportImage',
      'jumpNextError',
      'jumpPrevError',
      'increaseFontSize',
      'decreaseFontSize',
      'find',
      'findNext',
      'findPrevious',
      'findReplace',
    ],
  },
  design: {
    label: 'Design',
    actions: ['flushCaches', 'showAST', 'checkValidity'],
  },
  display: {
    label: 'Display',
    actions: [
      'viewAll',
      'zoomIn',
      'zoomOut',
      'toggleEdges',
      'toggleAxes',
      'toggleCrosshairs',
    ],
  },
  panels: {
    label: 'Panels',
    // A pair is one command with two keys, listed once.
    actions: [
      'toggleConsole',
      'toggleErrorLog',
      ['toggleExpertMode', 'toggleCodeEditor'],
      ['toggleParameters', 'toggleCustomizer'],
      'jumpToPanel',
      'nextPanel',
      'prevPanel',
    ],
  },
};

/**
 * Parse a keyboard event into a shortcut key object
 * @param {KeyboardEvent} event - Keyboard event
 * @returns {Object} Shortcut key object
 */
export function parseKeyboardEvent(event) {
  return {
    key: event.key,
    ctrl: event.ctrlKey,
    shift: event.shiftKey,
    alt: event.altKey,
    meta: event.metaKey,
  };
}

/**
 * Check if a shortcut matches a keyboard event
 * @param {Object} shortcut - Shortcut definition
 * @param {KeyboardEvent|Object} event - Keyboard event or parsed event
 * @returns {boolean}
 */
export function matchesShortcut(shortcut, event) {
  const parsed =
    event instanceof KeyboardEvent ? parseKeyboardEvent(event) : event;

  // Key must match (case-insensitive for letters)
  const keyMatches =
    shortcut.key.toLowerCase() === parsed.key.toLowerCase() ||
    shortcut.key === parsed.key;

  if (!keyMatches) return false;

  // Modifier checks (default to false if not specified)
  const ctrlMatch = (shortcut.ctrl || false) === (parsed.ctrl || false);
  const shiftMatch = (shortcut.shift || false) === (parsed.shift || false);
  const altMatch = (shortcut.alt || false) === (parsed.alt || false);
  const metaMatch = (shortcut.meta || false) === (parsed.meta || false);

  return ctrlMatch && shiftMatch && altMatch && metaMatch;
}

/**
 * Format a shortcut for display
 * @param {Object} shortcut - Shortcut definition
 * @param {Object} options - Formatting options
 * @returns {string}
 */
export function formatShortcut(shortcut, options = {}) {
  const { platform = detectPlatform() } = options;
  const parts = [];

  // Platform-specific modifier symbols
  const modifiers =
    platform === 'mac'
      ? { ctrl: '⌃', shift: '⇧', alt: '⌥', meta: '⌘' }
      : { ctrl: 'Ctrl', shift: 'Shift', alt: 'Alt', meta: 'Win' };

  if (shortcut.ctrl) parts.push(modifiers.ctrl);
  if (shortcut.shift) parts.push(modifiers.shift);
  if (shortcut.alt) parts.push(modifiers.alt);
  if (shortcut.meta) parts.push(modifiers.meta);

  // Format special keys
  let key = shortcut.key;
  const specialKeys = {
    Enter: platform === 'mac' ? '↩' : 'Enter',
    Escape: platform === 'mac' ? '⎋' : 'Esc',
    ArrowUp: '↑',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
    ' ': 'Space',
    Tab: platform === 'mac' ? '⇥' : 'Tab',
    Backspace: platform === 'mac' ? '⌫' : 'Backspace',
    Delete: platform === 'mac' ? '⌦' : 'Delete',
  };

  if (specialKeys[key]) {
    key = specialKeys[key];
  } else if (key.length === 1) {
    key = key.toUpperCase();
  }

  parts.push(key);

  return platform === 'mac' ? parts.join('') : parts.join('+');
}

/**
 * Detect the current platform
 * @returns {'mac' | 'windows' | 'linux'}
 */
export function detectPlatform() {
  if (typeof navigator === 'undefined') return 'windows';

  const platform = navigator.platform?.toLowerCase() || '';
  if (platform.includes('mac')) return 'mac';
  if (platform.includes('linux')) return 'linux';
  return 'windows';
}

/**
 * Keyboard configuration manager
 */
export class KeyboardConfig {
  constructor() {
    this.shortcuts = { ...DEFAULT_SHORTCUTS };
    this.handlers = {};
    this.enabled = true;
    this.listeners = [];
    // CSS selector used to detect whether focus is inside the code editor.
    // Shortcuts marked editorOnly only fire when the focused element matches
    // this selector or is a descendant of it, so that Ctrl+= / Ctrl+- do not
    // steal browser zoom when the editor does not have focus.
    this._editorSelector = '.cm-editor';

    // Bind event handler
    this._handleKeydown = this._handleKeydown.bind(this);
  }

  /**
   * Initialize keyboard shortcuts
   * @param {Object} options - Initialization options
   */
  init(options = {}) {
    // Load saved shortcuts
    this.load();

    // Apply any overrides from options
    if (options.shortcuts) {
      this.shortcuts = { ...this.shortcuts, ...options.shortcuts };
    }

    if (options.editorSelector) {
      this._editorSelector = options.editorSelector;
    }

    // Attach global listener
    document.addEventListener('keydown', this._handleKeydown);

    console.log('[Keyboard] Shortcuts initialized');
  }

  /**
   * Destroy the keyboard config and clean up
   */
  destroy() {
    document.removeEventListener('keydown', this._handleKeydown);
    this.handlers = {};
    this.listeners = [];
  }

  /**
   * Register a handler for an action
   * @param {string} action - Action name
   * @param {Function} handler - Handler function
   * @returns {Function} Unsubscribe function
   */
  on(action, handler) {
    if (!this.handlers[action]) {
      this.handlers[action] = [];
    }
    this.handlers[action].push(handler);

    // Return unsubscribe function
    return () => {
      this.handlers[action] = this.handlers[action].filter(
        (h) => h !== handler
      );
    };
  }

  /**
   * Remove a handler for an action
   * @param {string} action - Action name
   * @param {Function} handler - Handler to remove
   */
  off(action, handler) {
    if (this.handlers[action]) {
      this.handlers[action] = this.handlers[action].filter(
        (h) => h !== handler
      );
    }
  }

  /**
   * Add a change listener
   * @param {Function} listener - Change listener
   * @returns {Function} Unsubscribe function
   */
  addChangeListener(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /**
   * Notify change listeners
   * @private
   */
  _notifyChange() {
    for (const listener of this.listeners) {
      try {
        listener(this.shortcuts);
      } catch (error) {
        console.error('[Keyboard] Error in change listener:', error);
      }
    }
  }

  /**
   * Get shortcut for an action
   * @param {string} action - Action name
   * @returns {Object|null}
   */
  getShortcut(action) {
    return this.shortcuts[action] || null;
  }

  /**
   * Get all shortcuts
   * @returns {Object}
   */
  getAllShortcuts() {
    return { ...this.shortcuts };
  }

  /**
   * Set shortcut for an action
   * @param {string} action - Action name
   * @param {Object} shortcut - New shortcut definition
   */
  setShortcut(action, shortcut) {
    if (!DEFAULT_SHORTCUTS[action]) {
      console.warn(`[Keyboard] Unknown action: ${action}`);
      return;
    }

    const def = DEFAULT_SHORTCUTS[action];
    this.shortcuts[action] = {
      ...(def.editorOnly ? { editorOnly: true } : {}),
      ...shortcut,
      description: def.description,
    };

    this.save();
    this._notifyChange();
  }

  /**
   * Reset a shortcut to default
   * @param {string} action - Action name
   */
  resetShortcut(action) {
    if (DEFAULT_SHORTCUTS[action]) {
      this.shortcuts[action] = { ...DEFAULT_SHORTCUTS[action] };
      this.save();
      this._notifyChange();
    }
  }

  /**
   * Reset all shortcuts to defaults (OpenSCAD Desktop preset)
   */
  resetAll() {
    this.shortcuts = { ...DEFAULT_SHORTCUTS };
    this.save();
    this._notifyChange();
  }

  /**
   * Apply a named preset
   * @param {'openscad'|'legacy'} preset
   */
  applyPreset(preset) {
    if (preset === 'legacy') {
      this.shortcuts = { ...DEFAULT_SHORTCUTS };
      for (const [action, override] of Object.entries(LEGACY_FORGE_SHORTCUTS)) {
        if (this.shortcuts[action]) {
          this.shortcuts[action] = {
            ...this.shortcuts[action],
            ...override,
          };
        }
      }
    } else {
      // 'openscad' or unknown — use the OpenSCAD Desktop defaults
      this.shortcuts = { ...DEFAULT_SHORTCUTS };
    }
    this.save();
    this._notifyChange();
  }

  /**
   * Check if a shortcut has been customized
   * @param {string} action - Action name
   * @returns {boolean}
   */
  isCustomized(action) {
    const current = this.shortcuts[action];
    const defaultShortcut = DEFAULT_SHORTCUTS[action];
    if (!current || !defaultShortcut) return false;

    return (
      current.key !== defaultShortcut.key ||
      (current.ctrl || false) !== (defaultShortcut.ctrl || false) ||
      (current.shift || false) !== (defaultShortcut.shift || false) ||
      (current.alt || false) !== (defaultShortcut.alt || false) ||
      (current.meta || false) !== (defaultShortcut.meta || false)
    );
  }

  /**
   * Check for conflicting shortcuts
   * @param {Object} shortcut - Shortcut to check
   * @param {string} excludeAction - Action to exclude from conflict check
   * @returns {string|null} Conflicting action name or null
   */
  findConflict(shortcut, excludeAction = null) {
    for (const [action, existing] of Object.entries(this.shortcuts)) {
      if (action === excludeAction) continue;
      if (matchesShortcut(existing, shortcut)) {
        return action;
      }
    }
    return null;
  }

  /**
   * Enable/disable keyboard shortcuts
   * @param {boolean} enabled
   */
  setEnabled(enabled) {
    this.enabled = enabled;
  }

  /**
   * Save shortcuts to localStorage
   */
  save() {
    try {
      const customized = {};
      for (const [action, shortcut] of Object.entries(this.shortcuts)) {
        if (this.isCustomized(action)) {
          // Exclude behavioral flags (description, editorOnly) — they are
          // always re-applied from DEFAULT_SHORTCUTS on load.
          const { description: _desc, editorOnly: _eo, ...rest } = shortcut;
          customized[action] = rest;
        }
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(customized));
    } catch (error) {
      console.error('[Keyboard] Failed to save shortcuts:', error);
    }
  }

  /**
   * Load shortcuts from localStorage
   */
  load() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const customized = JSON.parse(saved);
        for (const [action, shortcut] of Object.entries(customized)) {
          const def = DEFAULT_SHORTCUTS[action];
          if (def) {
            this.shortcuts[action] = {
              ...(def.editorOnly ? { editorOnly: true } : {}),
              ...shortcut,
              description: def.description,
            };
          }
        }
      }
    } catch (error) {
      console.error('[Keyboard] Failed to load shortcuts:', error);
    }
  }

  /**
   * Handle keydown events
   * @private
   */
  _handleKeydown(event) {
    if (!this.enabled) return;

    // An Escape that has closed something (a drawer, a menu, a tooltip) is
    // not also Cancel render: closing the Customizer drawer while a preview
    // runs leaves the preview running.
    if (event.key === 'Escape' && event.defaultPrevented) return;

    const target = event.target;

    // Escape inside an open <dialog> belongs to the dialog: the browser
    // closes one on Escape unless the key's default is prevented, which
    // Cancel render did even with nothing rendering.
    if (event.key === 'Escape' && target.closest?.('dialog[open]')) return;

    const isInputField =
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable;

    // True when focus is inside the code editor (e.g. CodeMirror).
    // editorOnly shortcuts only fire here; when focus is elsewhere we skip
    // without calling preventDefault so browser zoom remains functional.
    const isInCodeEditor = Boolean(target.closest?.(this._editorSelector));

    // Find matching shortcut
    for (const [action, shortcut] of Object.entries(this.shortcuts)) {
      if (matchesShortcut(shortcut, event)) {
        if (shortcut.editorOnly) {
          // Only fire when focus is inside the code editor.
          // Skipping without preventDefault lets the browser handle the event.
          if (!isInCodeEditor) continue;
        } else if (isInputField && !shortcut.global) {
          // Skip non-global shortcuts in regular input fields (allow Escape).
          if (event.key !== 'Escape') continue;
        }

        // Prevent default browser behavior
        event.preventDefault();

        // Call handlers
        const handlers = this.handlers[action] || [];
        for (const handler of handlers) {
          try {
            handler(event);
          } catch (error) {
            console.error(`[Keyboard] Error in ${action} handler:`, error);
          }
        }

        return;
      }
    }
  }
}

/**
 * Singleton keyboard config instance
 */
export const keyboardConfig = new KeyboardConfig();

/**
 * Initialize keyboard shortcuts (convenience function)
 * @param {Object} options - Initialization options
 */
export function initKeyboardShortcuts(options = {}) {
  keyboardConfig.init(options);
  return keyboardConfig;
}

/**
 * The actions one list entry shows: a single action, or several keys for
 * one command listed together.
 * @param {string|string[]} entry - An item of a category's `actions`
 * @param {Object} shortcuts - All shortcuts by action
 * @returns {string[]} The entry's actions that have a shortcut
 */
function rowActions(entry, shortcuts) {
  return (Array.isArray(entry) ? entry : [entry]).filter(
    (action) => shortcuts[action]
  );
}

/**
 * Generate keyboard shortcuts help HTML (read-only)
 * @returns {string} HTML string
 */
export function generateShortcutsHelpHTML() {
  const shortcuts = keyboardConfig.getAllShortcuts();
  let html = '<div class="shortcuts-help">';

  for (const [categoryId, category] of Object.entries(SHORTCUT_CATEGORIES)) {
    html += `<div class="shortcuts-category" data-category="${categoryId}">`;
    html += `<h3>${category.label}</h3>`;
    html += '<dl>';

    for (const entry of category.actions) {
      const actions = rowActions(entry, shortcuts);
      if (actions.length === 0) continue;
      const keys = actions
        .map((action) => `<kbd>${formatShortcut(shortcuts[action])}</kbd>`)
        .join(', ');
      html += `<dt>${keys}</dt>`;
      html += `<dd>${shortcuts[actions[0]].description}</dd>`;
    }

    html += '</dl></div>';
  }

  html += '</div>';
  return html;
}

/**
 * Generate keyboard shortcuts editor HTML (with remapping)
 * @returns {string} HTML string
 */
export function generateShortcutsEditorHTML() {
  const shortcuts = keyboardConfig.getAllShortcuts();
  let html = '<div class="shortcuts-editor">';
  html += '<div class="shortcuts-preset-bar">';
  html +=
    '  <label for="shortcutsPresetSelect" class="shortcuts-preset-label">Preset:</label>';
  html +=
    '  <select id="shortcutsPresetSelect" class="shortcuts-preset-select">';
  html += '    <option value="openscad">OpenSCAD Desktop</option>';
  html += '    <option value="legacy">Legacy Forge</option>';
  html += '    <option value="custom">Custom</option>';
  html += '  </select>';
  html += '</div>';
  html +=
    '<p class="shortcuts-editor-intro">Click on a shortcut to change it. Press Escape to cancel.</p>';

  for (const [categoryId, category] of Object.entries(SHORTCUT_CATEGORIES)) {
    html += `<div class="shortcuts-category" data-category="${categoryId}">`;
    html += `<h3>${category.label}</h3>`;
    html += '<div class="shortcuts-list">';

    for (const entry of category.actions) {
      const actions = rowActions(entry, shortcuts);
      if (actions.length === 0) continue;
      const { description } = shortcuts[actions[0]];
      const customizedClass = actions.some((action) =>
        keyboardConfig.isCustomized(action)
      )
        ? ' shortcut-customized'
        : '';

      // The key is the button's name, so a screen reader hears "F6, button"
      // and can learn the shortcut; what pressing it does is its
      // description. An aria-label here replaced the key, so the key was
      // never spoken. A command with two keys is one row with a button per
      // key, each changed on its own.
      html += `<div class="shortcut-item${customizedClass}" data-action="${actions[0]}">`;
      html += `  <span class="shortcut-description">${description}</span>`;
      for (const action of actions) {
        html += `  <span class="sr-only" id="shortcut-change-${action}">Change shortcut for ${description}</span>`;
      }
      html += `  <div class="shortcut-key-group">`;
      for (const action of actions) {
        html += `    <button type="button" class="shortcut-key-btn" data-action="${action}" aria-describedby="shortcut-change-${action}">`;
        html += `      <kbd>${formatShortcut(shortcuts[action])}</kbd>`;
        html += `    </button>`;
        if (keyboardConfig.isCustomized(action)) {
          html += `    <button type="button" class="shortcut-reset-btn" data-action="${action}" aria-label="Reset to default" title="Reset to default">`;
          html += `      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">`;
          html += `        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>`;
          html += `        <path d="M3 3v5h5"/>`;
          html += `      </svg>`;
          html += `    </button>`;
        }
      }
      html += `  </div>`;
      html += `</div>`;
    }

    html += '</div></div>';
  }

  html += '</div>';
  return html;
}

/**
 * State for key capture mode
 */
let keyCaptureState = {
  active: false,
  action: null,
  button: null,
  originalContent: null,
  keydownHandler: null,
};

/**
 * Start capturing a new key binding
 * @param {string} action - Action to rebind
 * @param {HTMLElement} button - Button element that was clicked
 */
export function startKeyCapture(action, button) {
  // Cancel any existing capture
  if (keyCaptureState.active) {
    cancelKeyCapture();
  }

  keyCaptureState.active = true;
  keyCaptureState.action = action;
  keyCaptureState.button = button;
  keyCaptureState.originalContent = button.innerHTML;

  // Update button to show capture mode
  button.innerHTML = '<span class="key-capture-prompt">Press a key...</span>';
  button.classList.add('capturing');
  button.focus();

  // Temporarily disable the keyboard config to prevent shortcuts from firing
  keyboardConfig.setEnabled(false);

  // Create keydown handler
  keyCaptureState.keydownHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();

    // Escape cancels capture
    if (event.key === 'Escape') {
      cancelKeyCapture();
      return;
    }

    // Ignore modifier-only keys
    if (['Control', 'Shift', 'Alt', 'Meta'].includes(event.key)) {
      return;
    }

    // Parse the new shortcut
    const newShortcut = parseKeyboardEvent(event);

    // Check for conflicts
    const conflict = keyboardConfig.findConflict(newShortcut, action);
    if (conflict) {
      const conflictShortcut = keyboardConfig.getShortcut(conflict);
      showConflictWarning(button, conflictShortcut.description);
      return;
    }

    // Apply the new shortcut
    keyboardConfig.setShortcut(action, newShortcut);

    // End capture mode
    endKeyCapture(true);
  };

  // Add event listener
  document.addEventListener('keydown', keyCaptureState.keydownHandler, true);

  // Add click-outside handler to cancel
  setTimeout(() => {
    document.addEventListener('click', handleClickOutside, true);
  }, 0);
}

/**
 * Handle click outside the capture button
 */
function handleClickOutside(event) {
  if (
    keyCaptureState.active &&
    !keyCaptureState.button.contains(event.target)
  ) {
    cancelKeyCapture();
  }
}

/**
 * Cancel key capture mode
 */
export function cancelKeyCapture() {
  if (!keyCaptureState.active) return;

  // Restore button content
  if (keyCaptureState.button && keyCaptureState.originalContent) {
    keyCaptureState.button.innerHTML = keyCaptureState.originalContent;
    keyCaptureState.button.classList.remove('capturing');
  }

  endKeyCapture(false);
}

/**
 * End key capture mode
 * @param {boolean} success - Whether capture was successful
 */
function endKeyCapture(success) {
  // Remove event listeners
  if (keyCaptureState.keydownHandler) {
    document.removeEventListener(
      'keydown',
      keyCaptureState.keydownHandler,
      true
    );
  }
  document.removeEventListener('click', handleClickOutside, true);

  // Re-enable keyboard shortcuts
  keyboardConfig.setEnabled(true);

  // If successful, refresh the UI
  if (success) {
    refreshShortcutsEditor();
  }

  // Reset state
  keyCaptureState = {
    active: false,
    action: null,
    button: null,
    originalContent: null,
    keydownHandler: null,
  };
}

/**
 * Show conflict warning on a button
 * @param {HTMLElement} button - Button element
 * @param {string} conflictDescription - Description of conflicting action
 */
function showConflictWarning(button, conflictDescription) {
  button.innerHTML = `<span class="key-conflict-warning">Already used by: ${escapeHtml(conflictDescription)}</span>`;
  button.classList.add('conflict');

  setTimeout(() => {
    if (keyCaptureState.active && keyCaptureState.button === button) {
      button.innerHTML =
        '<span class="key-capture-prompt">Press a key...</span>';
      button.classList.remove('conflict');
    }
  }, 2000);
}

/**
 * Attach event listeners to shortcuts editor
 * @param {HTMLElement} container - Container element
 */
export function attachShortcutsEditorListeners(container) {
  // Key buttons - click to start capture
  container.querySelectorAll('.shortcut-key-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const action = btn.dataset.action;
      startKeyCapture(action, btn);
    });
  });

  // Reset buttons - click to reset individual shortcut
  container.querySelectorAll('.shortcut-reset-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const action = btn.dataset.action;
      keyboardConfig.resetShortcut(action);
      refreshShortcutsEditor();
    });
  });
}

/**
 * Refresh the shortcuts editor UI after a change
 */
export function refreshShortcutsEditor() {
  const container = document.getElementById('shortcutsModalBody');
  if (container) {
    container.innerHTML = generateShortcutsEditorHTML();
    attachShortcutsEditorListeners(container);
    _attachPresetListener(container);
  }
}

/**
 * Attach preset selector listener
 * @param {HTMLElement} container
 */
function _attachPresetListener(container) {
  const select = container.querySelector('#shortcutsPresetSelect');
  if (!select) return;
  select.addEventListener('change', () => {
    const preset = select.value;
    if (preset === 'custom') return;
    keyboardConfig.applyPreset(preset);
    refreshShortcutsEditor();
  });
}

/**
 * Initialize the shortcuts modal
 * @param {HTMLElement} modalBody - Modal body element
 * @param {Function} closeModal - Function to close the modal
 */
export function initShortcutsModal(modalBody, closeModal) {
  // Generate and insert the editor HTML
  modalBody.innerHTML = generateShortcutsEditorHTML();
  attachShortcutsEditorListeners(modalBody);
  _attachPresetListener(modalBody);

  // Handle Reset All button
  const resetAllBtn = document.getElementById('shortcutsResetAll');
  if (resetAllBtn) {
    resetAllBtn.addEventListener('click', () => {
      keyboardConfig.resetAll();
      refreshShortcutsEditor();
    });
  }

  // Handle Done button
  const doneBtn = document.getElementById('shortcutsModalDone');
  if (doneBtn) {
    doneBtn.addEventListener('click', closeModal);
  }

  // Handle overlay click
  const overlay = document.getElementById('shortcutsModalOverlay');
  if (overlay) {
    overlay.addEventListener('click', closeModal);
  }

  // Handle close button
  const closeBtn = document.getElementById('shortcutsModalClose');
  if (closeBtn) {
    closeBtn.addEventListener('click', closeModal);
  }
}
